import React, { useEffect, useRef, useState } from 'react'
import { SvgFunnel } from '../../assets/images'
import {
  DropdownType,
  filterOptionsConstant,
  NAMESPACE,
  OperatorSelectorTableFilterPropsType,
  PositionPropsState,
  SearchCriteria,
  TableColumnFilterPropsType,
  TableFilterModalContentPropsTypes,
} from '../../utils'
import CustomPopUpWithPortal from './CustomPopUpWithPortal'
import { isEmpty, omit } from 'lodash'
import CustomButton from '../CustomButton'
import { SelectDropDown } from '../select-dropdown/SearchDropDown'
import TypeBasedTableFilter from './TypeBasedTableFilter'
import useOutsideClick from '../hooks/useOutsideHook'

const OperatorSelectorTableFilter = ({
  tableName,
  filterObject,
  datatype = 'string',
  handleFilterSearchValue,
  handleFilterOperator,
}: OperatorSelectorTableFilterPropsType) => {
  const [filterOperator, setFilterOperator] = useState(filterObject?.operator)
  const handleOperatorSelector = (selectVal: DropdownType<string, string>) => {
    handleFilterOperator(selectVal?.value)
    setFilterOperator(selectVal?.value)
  }

  return (
    <div className="overflow-x-auto custom-scrollbar max-h-[150px] pt-3">
      <div className="mb-2">
        <SelectDropDown
          label="Operator"
          name="operator"
          placeholder="Select Operator"
          options={filterOptionsConstant?.[datatype]}
          defaultValue={filterOperator}
          onChange={handleOperatorSelector}
        />
      </div>
      <TypeBasedTableFilter
        tableName={tableName}
        filterCase={`${datatype}_${filterOperator}`}
        handleFilterSearchValue={handleFilterSearchValue}
        filterObject={filterObject}
      />
    </div>
  )
}

const TableFilterModalContent = <T,>({
  column,
  handleFilterSearchValue,
  handleFilterOperator,
  togglePopup,
  filterObject,
  resetFilter,
  applyFilter,
  tableName,
}: TableFilterModalContentPropsTypes<T>) => {
  const editRef = useRef<HTMLDivElement>(null)
  useOutsideClick({
    ref: editRef,
    handler: togglePopup,
  })
  return (
    <div
      ref={editRef}
      className="bg-accent-0 max-h-[300px] max-w-[250px] rounded custom-shadow p-3 font-light"
    >
      <h3>{NAMESPACE?.FILTER_BY}</h3>
      <OperatorSelectorTableFilter
        tableName={tableName}
        datatype={column?.dataType ?? 'string'}
        filterObject={filterObject}
        handleFilterSearchValue={handleFilterSearchValue}
        handleFilterOperator={handleFilterOperator}
      />
      <div className="flex justify-center gap-2 mt-2">
        <CustomButton
          dataQaId={`table_${tableName}_filter_clear`}
          color="primary"
          onClick={resetFilter}
        >
          <span className="text-xs font-light">{NAMESPACE?.CLEAR}</span>
        </CustomButton>
        <CustomButton
          dataQaId={`table_${tableName}_filter_apply`}
          color="primary"
          isFilled
          onClick={applyFilter}
        >
          <span className="text-xs font-light">{NAMESPACE?.APPLY_FILTER}</span>
        </CustomButton>
      </div>
    </div>
  )
}

const TableColumnFilter = <T,>({
  column,
  tableName,
  onFilterChange,
  filter,
}: TableColumnFilterPropsType<T>) => {
  const [popupData, setPopupData] = useState<PositionPropsState | null>(null)
  const [filterObject, setFilterObject] = useState<SearchCriteria>(
    !isEmpty(filter?.search_criteria?.[(column?.accessor as string) || ''])
      ? filter?.search_criteria?.[(column?.accessor as string) || '']
      : {
          search_key: (column?.accessor as string)?.toUpperCase() || '',
          search_value: [],
          operator:
            filterOptionsConstant?.[
              (column?.dataType as string) || 'string'
            ]?.[0]?.value || filterOptionsConstant?.['string']?.[0]?.value,
        }
  )
  const handleButtonClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e?.target as HTMLDivElement
    const buttonRect = target?.getBoundingClientRect()

    const popupPosition: PositionPropsState = {
      top: buttonRect.bottom + window.scrollY,
      left:
        buttonRect.left + window.scrollX - 150 < 150
          ? buttonRect.left + window.scrollX - 50
          : buttonRect.left + window.scrollX - 150,
    }

    setPopupData({ ...popupPosition })
  }
  const handleFilterSearchValue = (value: string[]) => {
    setFilterObject((prev) => ({
      ...prev,
      search_value: value,
    }))
  }
  const handleFilterOperator = (value: string) => {
    setFilterObject((prev) => ({
      ...prev,
      operator: value,
    }))
  }
  const resetFilter = () => {
    const filterClone = structuredClone(filter?.search_criteria)

    onFilterChange?.({
      ...filter,
      search_criteria: {
        ...omit(filterClone, `${(column?.accessor as string) || ''}`),
      },
    })
    setFilterObject((prev) => ({
      ...prev,
      search_value: [],
      operator:
        filterOptionsConstant?.[column?.dataType || 'string']?.[0]?.value ||
        filterOptionsConstant?.['string']?.[0]?.value,
    }))
    togglePopup()
  }
  const togglePopup = () => {
    setPopupData(null)
  }
  const applyFilter = () => {
    let filterClone = structuredClone(filter?.search_criteria)

    if (isEmpty(filterObject?.search_value)) {
      resetFilter()
    } else {
      filterClone = {
        ...filterClone,

        [column?.accessor || '']: {
          ...filterObject,
        },
      }

      onFilterChange?.({
        ...filter,
        search_criteria: {
          ...filterClone,
        },
      })
    }

    togglePopup()
  }

  useEffect(() => {
    setFilterObject(
      !isEmpty(filter?.search_criteria?.[(column?.accessor as string) || ''])
        ? filter?.search_criteria?.[(column?.accessor as string) || '']
        : {
            search_key: (column?.accessor as string)?.toUpperCase() || '',
            search_value: [],
            operator:
              filterOptionsConstant?.[column?.dataType ?? 'string']?.[0]
                ?.value || filterOptionsConstant?.['string']?.[0]?.value,
          }
    )
  }, [column])

  return (
    <div
      className={`${
        !isEmpty(filterObject?.search_value)
          ? 'svg-path-fill-filter-primary'
          : ''
      }`}
    >
      <div onClick={handleButtonClick}>
        <SvgFunnel className={'svg-path-fill-accent-300'} />
      </div>
      {!isEmpty(popupData) ? (
        <CustomPopUpWithPortal
          position={popupData}
          containerId={`table_filter_${tableName}`}
        >
          <TableFilterModalContent
            tableName={tableName}
            column={column}
            filterObject={filterObject}
            handleFilterSearchValue={handleFilterSearchValue}
            handleFilterOperator={handleFilterOperator}
            togglePopup={togglePopup}
            resetFilter={resetFilter}
            applyFilter={applyFilter}
          />
        </CustomPopUpWithPortal>
      ) : null}
    </div>
  )
}

export default TableColumnFilter
